/**
 * cart-membership.js — CartMembership
 * ─────────────────────────────────────────────────────────────────────────────
 * Owns cart-level membership transitions without a full page reload. Replaces the
 * legacy updateCart() / restrictVariantsInCart() / location.reload() logic that
 * lived inline in cart-summary-originew.liquid.
 *
 * Mechanism: the Shopify Sections Rendering API. Cart mutations (/cart/add.js,
 * /cart/change.js) are sent with a `sections` param; the rendered section HTML in
 * the response is swapped into the DOM via CartItems.renderSections() — the same
 * path quantity updates already use. No location.reload().
 *
 * UI wiring: delegated change/click listeners on `.cart__footer-wrapper` survive
 * section innerHTML swaps (inline scripts in cart-summary do not re-run).
 *
 * Source of truth is the live cart (fetched per operation), NOT customer.tags,
 * which lags Inveterate state by up to a session.
 *
 * Public API:
 *   window.FM.cartMembership.set('quarterly' | 'annual')
 *   window.FM.cartMembership.remove()
 *   window.FM.cartMembership.restrictDuplicates()
 *
 * Events (dispatched on document):
 *   fm:membership-gained        none        → cart
 *   fm:membership-lost          cart        → none   (member-only items cascade-removed)
 *   fm:membership-type-changed  quarterly  ↔ annual
 *   fm:membership-changed       any successful transition (always)
 *
 * Depends on: window.FM.config (fm-config.liquid), a <cart-items> element exposing
 * getSectionsToRender() + renderSections() (cart.js). Safe to load anywhere; it
 * no-ops the render step when no <cart-items> element is present.
 * ─────────────────────────────────────────────────────────────────────────────
 */
(function () {
  'use strict';

  const ENDPOINTS = {
    add: '/cart/add.js',
    update: '/cart/update.js',
    change: '/cart/change.js',
    get: '/cart.js',
  };
  const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const MEMBERSHIP_RADIO =
    'input[type="radio"][name="membershipOption"], input[type="radio"][name="membershipOption-mobile"]';

  function config() {
    return (window.FM && window.FM.config && window.FM.config.membership) || null;
  }

  function memberOnlyHandles() {
    return (window.FM && window.FM.config && window.FM.config.memberOnlyProducts) || [];
  }

  function currentMembership() {
    return (
      (window.FM && window.FM.state && window.FM.state.membership) ||
      { source: 'none', type: 'none', active: false }
    );
  }

  function membershipPlanFromRadioId(id) {
    if (!id) return null;
    if (id.includes('radio-non-member-unique')) return 'none';
    if (id.includes('radio-quarterly-member-unique')) return 'quarterly';
    if (id.includes('radio-annual-member-unique')) return 'annual';
    return null;
  }

  class CartMembership {
    constructor() {
      this.busy = false;
    }

    get cartItems() {
      return document.querySelector('cart-items');
    }

    // ── Public API ────────────────────────────────────────────────────────────

    /** Ensure exactly the given membership plan is in the cart. */
    set(type) {
      const cfg = config();
      if (!cfg || (type !== 'quarterly' && type !== 'annual')) {
        console.warn('[CartMembership] set(): invalid type or missing FM.config', type);
        return Promise.resolve();
      }

      const target = cfg[type];
      const other = cfg[type === 'quarterly' ? 'annual' : 'quarterly'];
      if (!target || !target.variantId) {
        console.warn('[CartMembership] set(): missing variantId for', type);
        return Promise.resolve();
      }

      return this._run(async () => {
        const cart = await this._getCart();
        const needsAdd = !this._membershipLine(cart, target.variantId);

        const updates = {};
        if (other && other.variantId && this._membershipLine(cart, other.variantId)) {
          updates[other.variantId] = 0;
        }
        if (this._variantQty(cart, target.variantId) > 1) {
          updates[target.variantId] = 1;
        }

        const hasUpdates = Object.keys(updates).length > 0;
        if (!needsAdd && !hasUpdates) return null;

        const next = { source: 'cart', type: type, active: true };
        let state;

        if (needsAdd && !hasUpdates) {
          state = await this._add(target.variantId, target.sellingPlanId, true);
        } else {
          if (needsAdd) await this._add(target.variantId, target.sellingPlanId, false);
          const cartAfter = await this._getCart();
          const postUpdates = {};
          if (other && other.variantId && this._membershipLine(cartAfter, other.variantId)) {
            postUpdates[other.variantId] = 0;
          }
          if (this._variantQty(cartAfter, target.variantId) > 1) {
            postUpdates[target.variantId] = 1;
          }
          state = await this._mutate(postUpdates, cartAfter);
          if (!state || !state.sections) state = await this._refreshSections();
        }

        this._afterTransition(next);
        this._renderAndSync(state);
        return state;
      });
    }

    /** Remove all membership SKUs and cascade-remove member-only items. */
    remove() {
      const cfg = config();

      return this._run(async () => {
        const cart = await this._getCart();
        const updates = {};

        if (cfg) {
          [cfg.quarterly, cfg.annual].forEach((plan) => {
            if (!plan || !plan.variantId) return;
            const line = this._membershipLine(cart, plan.variantId);
            if (line) updates[line.key] = 0;
          });
        }

        const handles = memberOnlyHandles();
        if (handles.length) {
          (cart.items || []).forEach((item) => {
            if (handles.indexOf(item.handle) !== -1) updates[item.key] = 0;
          });
        }

        if (!Object.keys(updates).length) return null;

        const state = await this._mutateKeys(updates);
        this._afterTransition({ source: 'none', type: 'none', active: false });
        this._renderAndSync(state);
        return state;
      });
    }

    /**
     * Enforce at most one membership plan, quantity 1. If both quarterly and annual
     * are present, keep the most recently added and drop the older. Replaces the
     * legacy restrictVariantsInCart() (which reloaded the page).
     */
    restrictDuplicates() {
      const cfg = config();
      if (!cfg) return Promise.resolve();

      return this._run(async () => {
        const cart = await this._getCart();
        const qLine = cfg.quarterly && cfg.quarterly.variantId ? this._membershipLine(cart, cfg.quarterly.variantId) : null;
        const aLine = cfg.annual && cfg.annual.variantId ? this._membershipLine(cart, cfg.annual.variantId) : null;
        const updates = {};

        if (qLine && aLine) {
          const items = cart.items || [];
          const older = items.indexOf(qLine) < items.indexOf(aLine) ? qLine : aLine;
          updates[older.key] = 0;
        }
        [qLine, aLine].forEach((line) => {
          if (line && line.quantity > 1 && updates[line.key] !== 0) {
            updates[line.key] = 1;
          }
        });

        if (!Object.keys(updates).length) return null;
        const state = await this._mutateKeys(updates);
        this._renderAndSync(state);
        return state;
      });
    }

    /**
     * Change or remove a single cart line in place — no reload. `ref` is either
     * { id: lineKey } or { line: lineNumber }. Used by the custom cart-item remove
     * (trash icon) and the line quantity selector. Re-derives membership from the
     * resulting cart so member pricing / strikethrough stays correct even when the
     * affected line was the membership itself.
     */
    mutateLine(ref, quantity) {
      if (!ref || (ref.id == null && ref.line == null)) return Promise.resolve(null);

      return this._run(async () => {
        const body = { quantity: Number(quantity) || 0, sections_url: window.location.pathname };
        if (ref.id != null) body.id = ref.id;
        else body.line = ref.line;

        const names = this._sectionNames();
        if (names.length) body.sections = names;

        const url = (window.routes && window.routes.cart_change_url) || ENDPOINTS.change;
        const removingMembership = (Number(quantity) || 0) === 0 && this._refIsMembershipLine(ref);
        const gatedUpdates = removingMembership ? this._memberOnlyLineUpdates() : {};
        let state = await fetch(url, {
          method: 'POST',
          headers: JSON_HEADERS,
          body: JSON.stringify(body),
        }).then((r) => {
          if (!r.ok) throw new Error('[CartMembership] mutateLine /cart/change.js failed: ' + r.status);
          return r.json();
        });

        if (!state || !state.sections) state = await this._refreshSections();

        await this._resyncMembershipFromCart();
        if (removingMembership && !this._isSubscriber()) {
          const cartAfter = await this._getCart();
          const updates = Object.assign({}, this._membershipLineUpdatesFromCart(cartAfter), gatedUpdates);
          if (Object.keys(updates).length) {
            const dropped = await this._mutateKeys(updates);
            if (dropped && dropped.sections) state = dropped;
          }
          await this._resyncMembershipFromCart();
        }
        this._render(state);
        this._syncMembershipDom();
        return state;
      });
    }

    // ── Internals ───────────────────────────────────────────────────────────────

    // Recompute FM.state.membership from the live cart after a line mutation that
    // may or may not have touched the membership line. Emits transition events and
    // keeps the strikethrough CSS (membership-in-cart) consistent with the cart.
    _resyncMembershipFromCart() {
      const cfg = config();
      return this._getCart().then((cart) => {
        let next = { source: 'none', type: 'none', active: false };
        if (cfg) {
          if (cfg.annual && cfg.annual.variantId && this._membershipLine(cart, cfg.annual.variantId)) {
            next = { source: 'cart', type: 'annual', active: true };
          } else if (cfg.quarterly && cfg.quarterly.variantId && this._membershipLine(cart, cfg.quarterly.variantId)) {
            next = { source: 'cart', type: 'quarterly', active: true };
          }
        }
        this._afterTransition(next);
      });
    }

    _run(task) {
      if (this.busy) return Promise.resolve();
      this.busy = true;
      this._setLoading(true);

      return Promise.resolve()
        .then(task)
        .catch((err) => {
          console.error('[CartMembership] transition failed:', err);
        })
        .finally(() => {
          this.busy = false;
          this._setLoading(false);
        });
    }

    _setLoading(on) {
      const ci = this.cartItems;
      if (ci) ci.classList.toggle('cart__items--disabled', !!on);
    }

    _getCart() {
      return fetch(ENDPOINTS.get, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    }

    _add(variantId, sellingPlanId, withSections) {
      const item = { id: variantId, quantity: 1 };
      if (sellingPlanId) item.selling_plan = sellingPlanId;

      const body = { items: [item] };
      if (withSections) {
        body.sections_url = window.location.pathname;
        const names = this._sectionNames();
        if (names.length) body.sections = names;
      }

      return fetch(ENDPOINTS.add, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify(body),
      })
        .then(async (r) => {
          const state = await r.json();
          if (!r.ok || (state.status && Number(state.status) >= 400)) {
            throw new Error(state.description || state.message || '[CartMembership] /cart/add.js failed');
          }
          if (withSections && !state.sections) {
            return this._refreshSections().then((refreshed) => refreshed || state);
          }
          return state;
        });
    }

    // Legacy updateCart() used /cart/change.js with line keys — required for
    // subscription (selling plan) lines. Build line-key updates from the live cart.
    _mutate(variantKeyedUpdates, cart) {
      return Promise.resolve(cart || this._getCart()).then((cartData) => {
        const keyed = this._lineKeysFromVariants(cartData, variantKeyedUpdates);
        return this._mutateKeys(keyed);
      });
    }

    _lineKeysFromVariants(cart, variantKeyedUpdates) {
      const keyed = {};
      Object.entries(variantKeyedUpdates || {}).forEach(([variantId, qty]) => {
        const line = this._membershipLine(cart, Number(variantId)) || this._line(cart, Number(variantId));
        if (line) keyed[line.key] = qty;
      });
      return keyed;
    }

    // Sequential /cart/change.js per line (legacy updateCart contract). Subscription
    // lines need line keys; batch /cart/update.js can leave items column stale.
    _changeLine(lineKey, quantity, sellingPlanId, withSections) {
      const body = {
        id: lineKey,
        quantity: quantity,
        sections_url: window.location.pathname,
      };
      if (quantity > 0 && sellingPlanId) body.selling_plan = sellingPlanId;

      if (withSections) {
        const names = this._sectionNames();
        if (names.length) body.sections = names;
      }

      const url = (window.routes && window.routes.cart_change_url) || ENDPOINTS.change;

      return fetch(url, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify(body),
      }).then((r) => {
        if (!r.ok) throw new Error('[CartMembership] /cart/change.js failed: ' + r.status);
        return r.json();
      });
    }

    _mutateKeys(updates) {
      if (!updates || !Object.keys(updates).length) return Promise.resolve(null);

      const entries = Object.entries(updates);
      let chain = Promise.resolve(null);

      entries.forEach(([key, qty], index) => {
        const isLast = index === entries.length - 1;
        chain = chain.then(() => this._changeLine(key, qty, null, isLast));
      });

      return chain;
    }

    _refreshSections() {
      const body = { updates: {}, sections_url: window.location.pathname };
      const names = this._sectionNames();
      if (!names.length) return Promise.resolve(null);
      body.sections = names;

      return fetch(ENDPOINTS.update, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify(body),
      }).then((r) => r.json());
    }

    _render(state) {
      if (state && typeof state.item_count !== 'number' && Array.isArray(state.items)) {
        state.item_count = state.items.reduce(function (n, item) {
          return n + (item.quantity || 0);
        }, 0);
      }

      const ci = this.cartItems;
      if (ci && typeof ci.renderSections === 'function' && state && state.sections) {
        ci.renderSections(state);
      }
    }

    _renderAndSync(state) {
      this._render(state);
      this._syncMembershipDom();
    }

    // Section swap replaces .custom_cart_items (drops membership-in-cart). Sync after
    // FM.state is updated so strikethrough CSS matches the sidebar selection.

    _syncMembershipDom() {
      this._syncShopSelector();
      const el = document.querySelector('.custom_cart_items');
      if (!el) return;
      const m = window.FM && window.FM.state && window.FM.state.membership;

      if (m && m.active && m.type === 'annual') {
        el.setAttribute('membership-in-cart', 'inveterate-subscription-1');
      } else if (m && m.active && m.type === 'quarterly') {
        el.setAttribute('membership-in-cart', 'inveterate-subscription');
      } else {
        el.removeAttribute('membership-in-cart');
      }
    }

    // The "Choose the way you shop" radios are the visible membership control.
    // Removing the membership line has to select Shop as a Non-Member, or the
    // member plan stays selected and the membership product remains in the cart.
    _syncShopSelector() {
      const m = currentMembership();
      const type = m.active && (m.type === 'quarterly' || m.type === 'annual') ? m.type : 'none';

      document.querySelectorAll(MEMBERSHIP_RADIO).forEach((input) => {
        const plan = membershipPlanFromRadioId(input.id);
        if (!plan) return;
        input.checked = plan === type;
      });

      document.querySelectorAll('.option-non-member-unique').forEach((el) => {
        el.classList.toggle('active', type === 'none');
      });
      document.querySelectorAll('.option-quarterly-member-unique').forEach((el) => {
        const on = type === 'quarterly';
        el.classList.toggle('checked', on);
        el.classList.toggle('active', on);
      });
      document.querySelectorAll('.option-annual-member-unique').forEach((el) => {
        el.classList.toggle('active', type === 'annual');
      });
      document.querySelectorAll('.small-toggle__checkbox').forEach((el) => {
        el.checked = type !== 'none';
      });
    }

    _membershipLineUpdatesFromCart(cart) {
      const updates = {};
      (cart.items || []).forEach((item) => {
        if (item.handle === 'inveterate-subscription' || item.handle === 'inveterate-subscription-1') {
          updates[item.key] = 0;
        }
      });
      return updates;
    }

    // Request only real section instance ids from the DOM — template keys like cart-items
    // are resolve-only (_itemsSectionIds) and break cart API section responses if included.
    _sectionNames() {
      const ci = this.cartItems;
      if (!ci || typeof ci.getSectionsToRender !== 'function') return [];
      const names = (ci.getSectionsToRender() || []).map(function (s) {
        return s.section;
      });
      return names.filter(function (name, index, arr) {
        return name && arr.indexOf(name) === index;
      });
    }

    _isSubscriber() {
      const el = document.getElementById('fm-state');
      return !!(el && el.getAttribute('data-fm-subscriber') === 'true');
    }

    _memberOnlyLineUpdates() {
      const updates = {};
      document.querySelectorAll('[data-member-exclusive="true"][data-key]').forEach((el) => {
        const key = el.getAttribute('data-key');
        if (key) updates[key] = 0;
      });
      return updates;
    }

    _refIsMembershipLine(ref) {
      if (!ref) return false;
      let row = null;
      if (ref.id != null) {
        row = document.querySelector('.custom_cart_item[data-key="' + String(ref.id).replace(/"/g, '\\"') + '"]');
      } else if (ref.line != null) {
        const sel = document.querySelector('.quantity_selector[data-line="' + String(ref.line) + '"]');
        row = sel && sel.closest('.custom_cart_item');
      }
      return !!(row && row.classList.contains('membership_item'));
    }

    _line(cart, variantId) {
      return (cart.items || []).find((i) => i.variant_id === variantId) || null;
    }

    // Match subscription lines by selling plan when possible (legacy change.js behavior).
    _membershipLine(cart, variantId) {
      const cfg = config();
      if (!cfg) return this._line(cart, variantId);

      const plans = [cfg.quarterly, cfg.annual].filter(Boolean);
      for (let i = 0; i < plans.length; i++) {
        const plan = plans[i];
        if (plan.variantId !== variantId) continue;
        const withPlan = (cart.items || []).find((item) => {
          if (item.variant_id !== variantId) return false;
          if (!plan.sellingPlanId) return true;
          const planId = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
            ? item.selling_plan_allocation.selling_plan.id
            : null;
          return planId && Number(planId) === Number(plan.sellingPlanId);
        });
        if (withPlan) return withPlan;
      }

      return this._line(cart, variantId);
    }

    _variantQty(cart, variantId) {
      const line = this._membershipLine(cart, variantId);
      return line ? line.quantity : 0;
    }

    _afterTransition(next) {
      const prev = currentMembership();

      if (window.FM) {
        window.FM.state = window.FM.state || {};
        const merged = Object.assign({}, window.FM.state.membership, next);
        if (!next.active) merged.hasMemberOnlyItems = false;
        window.FM.state.membership = merged;
      }

      const detail = Object.assign({ previous: prev }, next);
      if (!prev.active && next.active) this._emit('fm:membership-gained', detail);
      else if (prev.active && !next.active) this._emit('fm:membership-lost', detail);
      else if (prev.type !== next.type) this._emit('fm:membership-type-changed', detail);

      this._emit('fm:membership-changed', detail);
    }

    _emit(name, detail) {
      document.dispatchEvent(new CustomEvent(name, { detail: detail }));
    }
  }

  function injectMembershipItemStyles() {
    if (document.getElementById('fm-membership-item-price-fix')) return;
    const style = document.createElement('style');
    style.id = 'fm-membership-item-price-fix';
    style.textContent =
      '.membership_item .custom_cart_item_price_member .money { text-decoration: none !important; }';
    document.head.appendChild(style);
  }

  function initMembershipUI() {
    injectMembershipItemStyles();
    const root = document.querySelector('.cart__footer-wrapper');
    if (!root || root.dataset.fmMembershipBound === 'true') return;
    root.dataset.fmMembershipBound = 'true';

    root.addEventListener('change', onMembershipRadioChange);
    root.addEventListener('mousedown', onMembershipRadioMouseDown);
    root.addEventListener('click', onMembershipRadioClick);

    const cm = window.FM && window.FM.cartMembership;
    if (cm) cm.restrictDuplicates();
  }

  function onMembershipRadioChange(event) {
    const target = event.target;
    if (!target.matches(MEMBERSHIP_RADIO)) return;

    const cm = window.FM && window.FM.cartMembership;
    if (!cm || cm.busy) return;

    const plan = membershipPlanFromRadioId(target.id);
    if (plan === 'none') cm.remove();
    else if (plan === 'quarterly') cm.set('quarterly');
    else if (plan === 'annual') cm.set('annual');
  }

  function onMembershipRadioMouseDown(event) {
    const radio = event.target.closest('input[type="radio"][id*="radio-annual-member-unique"]');
    if (radio) radio.dataset.wasChecked = radio.checked ? '1' : '0';
  }

  function onMembershipRadioClick(event) {
    const toggle = event.target.closest('.small-toggle__checkbox');
    if (toggle) {
      const cm = window.FM && window.FM.cartMembership;
      if (!cm || cm.busy) return;
      if (toggle.checked) cm.set('annual');
      else cm.remove();
      return;
    }

    const radio = event.target.closest('input[type="radio"][id*="radio-annual-member-unique"]');
    if (!radio || radio.dataset.wasChecked !== '1') return;

    const cm = window.FM && window.FM.cartMembership;
    if (!cm || cm.busy) return;
    cm.remove();
  }

  window.FM = window.FM || {};
  window.FM.CartMembership = CartMembership;
  window.FM.cartMembership = window.FM.cartMembership || new CartMembership();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMembershipUI);
  } else {
    initMembershipUI();
  }
})();
